import React, { useState, useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import { Button, Input, Popup } from 'components/UIElements'
import { blockTypeIcon, waveForm } from 'assets/assets'
import { MEDIA_TYPE_MAP, MODAL_CLASSES_MAP } from 'utils/constants'
import 'styles/d3/richtexteditor.scss'
import { getSelectedBlockElement } from 'utils/richText'
import MediaUpload from '../containers/mediaUploadContainer'

const BLOCK_TYPES = [
  { label: 'Regular', node: 'paragraph' },
  { label: 'Heading 1', node: 'heading', attrs: { level: 1 } },
  { label: 'Heading 2', node: 'heading', attrs: { level: 2 } },
  { label: 'Heading 3', node: 'heading', attrs: { level: 3 } },
]

const ALIGNMENT_TYPES = [
  { type: 'left', class: 'align-left' },
  { type: 'center', class: 'align-center' },
]

const NO_URL_TEXT = 'No URL'
const URL_TEXT_PLACEHOLDER = 'Link text'
const URL_LINK_PLACEHOLDER = 'Which URL should the text refer to?'
const URL_LINK_HELPER_TEXT = 'Include http:// or https://'
const CHECK_YOUR_LINKS = 'Check your links'
const LINK_CHECK_REMINDER = 'They cannot be revised after deployment'

function stripHtml(html) {
  if (!html) return ''
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

export default function RichTextEditor(props) {
  const {
    value = '',
    onChange,
    placeholder = '',
    readOnly = false,
    showToolbar = true,
    hasBlockStyles,
    hasAlignmentControls,
    hasLinks,
    hasError,
    className = '',
    charLimit,
    noStyling,
    onBlur,
    onFocus,
    spellCheck = true,
    stripPastedStyles,
    isChoice,
    canAddImage,
    canAddAudio,
    canAddVideo,
    openModal,
    closeModal,
    addItemImage,
    addItemMedia,
    deleteItemImage,
    deleteItemMedia,
    itemId,
    item,
    currentResourcesSizeCount,
  } = props

  const [editorHasFocus, setEditorHasFocus] = useState(false)
  const [showURLInput, setShowURLInput] = useState(false)
  const [showURLOptions, setShowURLOptions] = useState(false)
  const [urlValue, setUrlValue] = useState('')
  const [urlText, setUrlText] = useState('')
  const [urlMenuPosition, setUrlMenuPosition] = useState({ x: 0, y: 0 })
  const [image, setImage] = useState(item?.image ?? null)
  const [audio, setAudio] = useState(item?.resources?.audio ?? null)
  const [video, setVideo] = useState(item?.resources?.video ?? null)
  const [mediaSrc, setMediaSrc] = useState(item?.mediaSrc ?? null)
  const [src, setSrc] = useState(item?.imageSrc ?? null)
  const editorWrapperRef = useRef(null)
  const editorParentRef = useRef(null)
  const urlTextFieldRef = useRef(null)
  const lastHtmlRef = useRef(value)

  const setLinkMenuPosition = useCallback(
    e => {
      if (!editorWrapperRef.current) return
      const { x: parentX, y: parentY } = editorWrapperRef.current.getBoundingClientRect()
      const selection = window.getSelection()
      const blockEl = getSelectedBlockElement()
      const blockRect = blockEl?.getBoundingClientRect()
      const selRect = selection?.rangeCount ? selection.getRangeAt(0).getBoundingClientRect() : null
      const noSelection = selection?.type === 'None'
      const x = (noSelection ? blockRect?.x : selRect?.x) ?? blockRect?.x ?? parentX
      const y = (noSelection ? blockRect?.y : selRect?.y) ?? blockRect?.y ?? parentY
      setUrlMenuPosition({ x: x - parentX, y: y - parentY + 24 })
    },
    [],
  )

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Underline,
    ],
    content: value || '',
    editable: !readOnly,
    editorProps: {
      handleDOMEvents: {
        focus: () => { setEditorHasFocus(true); onFocus?.() },
        blur: () => { setEditorHasFocus(false); onBlur?.() },
      },
    },
    onUpdate: ({ editor: ed }) => {
      let html = ed.getHTML()
      const textLen = stripHtml(html).length
      if (charLimit && textLen > charLimit) {
        ed.commands.setContent(lastHtmlRef.current, false)
        return
      }
      lastHtmlRef.current = html
      if (onChange) onChange(html)
    },
    editorProps: {
      attributes: {
        'data-placeholder': placeholder,
        'data-spellcheck': spellCheck,
      },
      handlePaste(view, event) {
        if (!charLimit) return false
        const pastedText = event.clipboardData?.getData('text/plain') || ''
        const currentLen = stripHtml(lastHtmlRef.current).length
        const selection = view.state.selection
        const from = selection.from
        const to = selection.to
        const selectedText = view.state.doc.textBetween(from, to, ' ')
        if (currentLen + pastedText.length - selectedText.length > charLimit) {
          const allowed = charLimit - currentLen + selectedText.length
          if (allowed <= 0) return true
          event.clipboardData.setData('text/plain', pastedText.slice(0, allowed))
        }
        return false
      },
    },
  }, [placeholder, readOnly, charLimit])

  useEffect(() => {
    if (editor && value !== undefined && value !== lastHtmlRef.current) {
      lastHtmlRef.current = value
      editor.commands.setContent(value || '', false)
    }
  }, [value, editor])

  useEffect(() => {
    const listener = e => {
      if (editorParentRef.current && !editorParentRef.current.contains(e.target)) {
        setShowURLInput(false)
        setShowURLOptions(false)
      }
    }
    document.addEventListener('mousedown', listener)
    return () => document.removeEventListener('mousedown', listener)
  }, [])

  useEffect(() => {
    if (!item?.image) setImage(null)
    if (!item?.resources) setAudio(null) || setVideo(null)
  }, [item?.image, item?.resources])

  const handleFocus = useCallback(() => {
    setEditorHasFocus(true)
    if (onFocus) onFocus()
  }, [onFocus])

  const handleBlur = useCallback(() => {
    setEditorHasFocus(false)
    if (onBlur) onBlur()
  }, [onBlur])

  const toggleBold = e => {
    e.preventDefault()
    editor?.chain().focus().toggleBold().run()
  }
  const toggleItalic = e => {
    e.preventDefault()
    editor?.chain().focus().toggleItalic().run()
  }
  const toggleUnderline = e => {
    e.preventDefault()
    editor?.chain().focus().toggleUnderline().run()
  }
  const toggleOrderedList = e => {
    e.preventDefault()
    editor?.chain().focus().toggleOrderedList().run()
  }
  const toggleBulletList = e => {
    e.preventDefault()
    editor?.chain().focus().toggleBulletList().run()
  }
  const setBlockType = (node, attrs) => e => {
    e.preventDefault()
    if (node === 'paragraph') editor?.chain().focus().setParagraph().run()
    else editor?.chain().focus().setHeading(attrs).run()
  }
  const setAlignment = align => e => {
    e.preventDefault()
    editor?.chain().focus().setTextAlign(align).run()
  }

  const promptForLink = (e, isToolbarClick = false) => {
    e.preventDefault()
    if (readOnly) return
    const attrs = editor?.getAttributes('link') ?? {}
    const href = attrs.href || ''
    const { from, to } = editor?.state.selection ?? {}
    const linkText = from != null && to != null ? editor?.state.doc.textBetween(from, to, ' ') || '' : ''
    setLinkMenuPosition(e)
    setUrlValue(href)
    setUrlText(linkText)
    if (isToolbarClick) {
      if (href) setShowURLOptions(true)
      else setShowURLInput(true)
    } else if (href) setShowURLOptions(true)
    if (!href) setTimeout(() => urlTextFieldRef.current?.focus(), 0)
  }

  const confirmLink = e => {
    e.preventDefault()
    const text = urlText || urlValue
    const opts = { href: urlValue, target: '_blank', rel: 'noopener noreferrer' }
    if (text) {
      editor?.chain().focus().insertContent(text).setLink(opts).run()
    } else {
      editor?.chain().focus().setLink(opts).run()
    }
    onChange?.(editor?.getHTML() ?? '')
    setShowURLInput(false)
    setShowURLOptions(false)
    setUrlValue('')
    setUrlText('')
  }

  const removeLink = e => {
    e.preventDefault()
    editor?.chain().focus().unsetLink().run()
    onChange?.(editor?.getHTML() ?? '')
    setShowURLInput(false)
    setShowURLOptions(false)
  }

  const toggleMediaUploader = (mediaType = MEDIA_TYPE_MAP.image) => {
    const allowed = [canAddImage && MEDIA_TYPE_MAP.image, canAddAudio && MEDIA_TYPE_MAP.audio, canAddVideo && MEDIA_TYPE_MAP.video].filter(Boolean)
    const itemCallbacks = {
      audio: payload => insertMedia(payload),
      image: payload => insertImage(payload),
      video: payload => insertMedia(payload, true),
    }
    openModal?.({
      content: (
        <MediaUpload
          {...props}
          itemCallbacks={itemCallbacks}
          onClose={closeModal}
          mediaTypes={allowed}
          initTab={mediaType}
          currentResourcesSizeCount={currentResourcesSizeCount}
        />
      ),
      noButtons: true,
      className: MODAL_CLASSES_MAP.uploadMedia,
      closeOnBackgroundClick: false,
    })
  }

  const insertImage = imageObj => {
    const { imageId, size, src: imgSrc } = imageObj
    setImage(imageId)
    setSrc(imgSrc)
    addItemImage?.({ itemId, imageId, imageSrc: imgSrc, size })
    closeModal?.()
  }

  const insertMedia = (mediaObj, isVideo = false) => {
    const { fileId, size, src: mediaSrcVal } = mediaObj
    if (isVideo) setVideo(fileId)
    else setAudio(fileId)
    setMediaSrc(mediaSrcVal)
    addItemMedia?.({ itemId, fileId, fileSrc: mediaSrcVal, size, isVideo })
    closeModal?.()
  }

  const currentContentLength = editor ? stripHtml(editor.getHTML()).length : 0
  const canUploadMedia = canAddImage || canAddAudio
  const isMultiLine = (value || '').includes('</p>') || (value || '').includes('<br')
  const addExtraMargin = isMultiLine && canUploadMedia && editorHasFocus

  const blockType = editor?.state.selection.$from.parent.type.name === 'paragraph'
    ? 'paragraph'
    : editor?.state.selection.$from.parent.type.name === 'heading'
      ? `heading-${editor.state.selection.$from.parent.attrs.level}`
      : 'paragraph'

  const blockStyleControls = () => (
    <Popup dark noPointer useOnMousedown position='bottom' trigger={<button type='button'>{blockTypeIcon}</button>}>
      {BLOCK_TYPES.map(({ label, node, attrs }) => {
        const isActive = (node === 'paragraph' && blockType === 'paragraph') || (node === 'heading' && blockType === `heading-${attrs?.level}`)
        return (
          <button
            key={label}
            type='button'
            className={`${isActive ? 'active' : ''} flexed`}
            onMouseDown={setBlockType(node, attrs || {})}
          >
            {label}
            {isActive && <i className='fas fa-check' />}
          </button>
        )
      })}
    </Popup>
  )

  const richTextControls = () => (
    <>
      {!isChoice && (
        <>
          <button
            tabIndex={-1}
            type='button'
            aria-label='ol-block'
            className={`fas fa-list-ol${editor?.isActive('orderedList') ? ' active' : ''}`}
            onMouseDown={e => { e.preventDefault(); editor?.chain().focus().toggleOrderedList().run() }}
          />
          <button
            tabIndex={-1}
            type='button'
            aria-label='ul-block'
            className={`fas fa-list-ul${editor?.isActive('bulletList') ? ' active' : ''}`}
            onMouseDown={e => { e.preventDefault(); editor?.chain().focus().toggleBulletList().run() }}
          />
        </>
      )}
      <button
        tabIndex={-1}
        type='button'
        aria-label='bold'
        className={`fas fa-bold${editor?.isActive('bold') ? ' active' : ''}`}
        onMouseDown={toggleBold}
      />
      <button
        tabIndex={-1}
        type='button'
        aria-label='italic'
        className={`fas fa-italic${editor?.isActive('italic') ? ' active' : ''}`}
        onMouseDown={toggleItalic}
      />
      <button
        tabIndex={-1}
        type='button'
        aria-label='underline'
        className={`fas fa-underline${editor?.isActive('underline') ? ' active' : ''}`}
        onMouseDown={toggleUnderline}
      />
    </>
  )

  const alignStyleControls = () =>
    ALIGNMENT_TYPES.map(({ type }) => (
      <button
        key={type}
        type='button'
        className={`fa-solid fa-${type === 'left' ? 'align-left' : 'align-center'}${editor?.isActive({ textAlign: type }) ? ' active' : ''}`}
        onMouseDown={e => { e.preventDefault(); setAlignment(type)(e) }}
      />
    ))

  const linkMenu = () => (
    <div
      className='link-menu'
      style={{ left: `${urlMenuPosition.x}px`, top: `${urlMenuPosition.y}px` }}
    >
      <div className={`link-options flexed column${showURLOptions ? '' : ' hidden'}`}>
        {urlValue ? (
          <a href={urlValue} rel='noopener noreferrer' target='_blank' className='word-clamp'>{urlValue}</a>
        ) : (
          <div className='word-clamp'>{NO_URL_TEXT}</div>
        )}
        {!readOnly && (
          <>
            <button type='button' onMouseDown={() => { setShowURLInput(true); setShowURLOptions(false); setTimeout(() => urlTextFieldRef.current?.focus(), 0) }}>Edit link</button>
            <button type='button' id='remove-link' onMouseDown={removeLink}>Remove</button>
          </>
        )}
        <div className='link-check-message'>
          <i className='fas fa-info-circle' />
          <span className='check-your-links'>{CHECK_YOUR_LINKS}</span>
          <span className='warning-message'>{LINK_CHECK_REMINDER}</span>
        </div>
      </div>
      {showURLInput && (
        <div className='link-editor'>
          <div className='flexed'>
            <label htmlFor='link-text-input'>Link text</label>
            <Input
              id='link-text-input'
              ref={urlTextFieldRef}
              value={urlText}
              onChange={setUrlText}
              placeholder={URL_TEXT_PLACEHOLDER}
              onEnterPress={confirmLink}
            />
          </div>
          <div className='flexed'>
            <label htmlFor='link-val-input'>URL address</label>
            <Input
              id='link-val-input'
              value={urlValue}
              onChange={setUrlValue}
              placeholder={URL_LINK_PLACEHOLDER}
              onEnterPress={confirmLink}
            />
          </div>
          <div className='flexed'><span>{URL_LINK_HELPER_TEXT}</span></div>
        </div>
      )}
    </div>
  )

  const mediaUploadButtons = (multiLine = false) => (
    <div className={`media-upload-button-list flexed end-justified${editorHasFocus ? '' : ' none'}${multiLine ? ' multi-line' : ''}`}>
      {canAddImage && (
        <button type='button' className='clickable' onMouseDown={() => toggleMediaUploader()}>
          <i className='far fa-image' />
          <div className='helper-popup'>Upload Image</div>
        </button>
      )}
      {canAddAudio && (
        <button type='button' className='clickable' onMouseDown={() => toggleMediaUploader(MEDIA_TYPE_MAP.audio)}>
          {waveForm}
          <div className='helper-popup'>Upload Audio</div>
        </button>
      )}
      {canAddVideo && (
        <button type='button' className='clickable' onMouseDown={() => toggleMediaUploader(MEDIA_TYPE_MAP.video)}>
          <i className='fas fa-video' />
          <div className='helper-popup'>Upload Video</div>
        </button>
      )}
    </div>
  )

  if (!editor) return null

  return (
    <div className='d3-editor-wrapper' ref={editorParentRef}>
      {hasLinks && linkMenu()}
      <div
        ref={editorWrapperRef}
        className={`${className ? `${className} ` : ''}d3-editor${editorHasFocus ? ' focus' : ''}${readOnly ? ' read-only' : ''}${hasError ? ' error' : ''}${addExtraMargin ? ' extra-margin' : ''}`}
        onClick={e => {
          if (hasLinks) promptForLink(e)
          editor.commands.focus()
        }}
      >
        <EditorContent editor={editor} />
        {!image && !audio && !video && canUploadMedia && mediaUploadButtons(isMultiLine)}
        {showToolbar && (
          <div className={`rich-text-toolbar${editorHasFocus ? '' : ' none'}`}>
            {richTextControls()}
            {hasBlockStyles && blockStyleControls()}
            {hasAlignmentControls && alignStyleControls()}
            {hasLinks && (
              <button tabIndex={-1} type='button' id='text-link' className='fas fa-link' onMouseDown={e => promptForLink(e, true)} />
            )}
          </div>
        )}
        {image && (
          <div className='image-container question-image flexed column'>
            <div className='image flexed column'>
              <Button className='fas fa-times remove-image' onClick={() => { deleteItemImage?.(itemId, image); setImage(null) }} />
              <p>{image}</p>
              <img src={src} alt='uploaded-question' />
            </div>
          </div>
        )}
        {audio && (
          <div className='media-container question-media flexed column'>
            <div className='media flexed column'>
              <Button className='fas fa-times remove-media' onClick={() => { deleteItemMedia?.(itemId, audio); setAudio(null); setVideo(null) }} />
              <p>{audio}</p>
              <audio src={mediaSrc} controls />
            </div>
          </div>
        )}
        {video && (
          <div className='media-container question-media flexed column'>
            <div className='media flexed column'>
              <Button className='fas fa-times remove-media' onClick={() => { deleteItemMedia?.(itemId, video, true); setVideo(null) }} />
              <p>{video}</p>
              <video src={mediaSrc} controls><track kind='captions' /></video>
            </div>
          </div>
        )}
      </div>
      {charLimit != null && <span className='char-limit'>{`${currentContentLength}/${charLimit}`}</span>}
    </div>
  )
}

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  showToolbar: PropTypes.bool,
  hasBlockStyles: PropTypes.bool,
  hasAlignmentControls: PropTypes.bool,
  hasLinks: PropTypes.bool,
  hasError: PropTypes.bool,
  className: PropTypes.string,
  charLimit: PropTypes.number,
  noStyling: PropTypes.bool,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  spellCheck: PropTypes.bool,
  stripPastedStyles: PropTypes.bool,
  isChoice: PropTypes.bool,
  canAddImage: PropTypes.bool,
  canAddAudio: PropTypes.bool,
  canAddVideo: PropTypes.bool,
  openModal: PropTypes.func,
  closeModal: PropTypes.func,
  addItemImage: PropTypes.func,
  addItemMedia: PropTypes.func,
  deleteItemImage: PropTypes.func,
  deleteItemMedia: PropTypes.func,
  itemId: PropTypes.string,
  item: PropTypes.object,
  currentResourcesSizeCount: PropTypes.number,
}
